import { Collection, Customer, Product, Variant } from '../shop';
import { Cart, Line } from '../shop/models/cart.model';

const PageViewAsync = async (pageType: string = 'Other', customer?: Customer) => {
  const sh_info_obj = {
    event: 'sh_info',
    page_type: pageType,
    page_currency: 'EUR',
    user: {
      type: 'visitor',
    } as any,
  };
  if (customer) {
    sh_info_obj.user = {
      id: customer.id.split('/').pop(),
      first_name: customer.firstName,
      last_name: customer.lastName,
      email_sha256: await hashSha256(customer.email),
      email_sha1: await hashSha1(customer.email),
      email: null,
      marketing: customer.acceptsMarketing,
    };
  }

  (<any>window).dataLayer.push(sh_info_obj);
};

const CollectionPageView = (products: Partial<Product>[], collection?: Collection) => {
  const productBrands = products.map(a => a.vendor);
  const productTypes = products.map(a => a.productType);
  const productSkus = products.map(a => a.variants?.[0].sku);
  const productNames = products.map(a => a.title?.replace(/["']/g, ''));
  const productHandles = products.map(a => a.handle);
  const productIds = products.map(a => a.id?.split('/').pop());
  const productPrices = products.map(a => a.priceRange?.minVariantPrice.amount);
  const productPositions = products.map((a, i) => i + 1);
  const gProductIds = products.map(a => `shopify_NL_${a.id}_${a.variants?.[0].id.split('/').pop()}`);
  const variantIds = products.map(a => a.variants?.[0].id.split('/').pop());
  const variantTitles = products.map(a => a.variants?.[0].title);

  (<any>window).dataLayer.push({
    event: 'ee_productImpression',
    category_name: collection?.title,
    category_id: collection?.id.split('/').pop(),
    category_handle: collection?.handle,
    product_brand: productBrands,
    product_type: productTypes,
    product_sku: productSkus,
    product_name: productNames,
    product_handle: productHandles,
    product_id: productIds,
    product_price: productPrices,
    currency: 'EUR',
    product_position: productPositions,
    g_product_id: gProductIds,
    variant_id: variantIds,
    variant_title: variantTitles,
  });
};

const ProductPageView = (product: Partial<Product>, variant?: Variant) => {
  const lastCollection = product.collections?.[product.collections?.length - 1];
  const availableVariantCount = product.variants?.filter(v => v.availableForSale).length;

  (<any>window).dataLayer.push({
    event: 'ee_productDetail',
    product_id: product.id?.split('/').pop(),
    product_name: product.title?.replace(/["']/g, ''),
    product_handle: product.handle,
    product_type: product.productType,
    product_brand: product.vendor,
    product_sku: variant?.sku || product.variants?.[0].sku,
    product_price: variant?.price?.amount || product.variants?.[0].price?.amount,
    currency: 'EUR',
    category_id: lastCollection?.id.split('/').pop(),
    category_name: lastCollection?.title,
    category_handle: lastCollection?.handle,
    g_product_id: `shopify_NL_${product.id}_${(variant || product.variants?.[0])?.id.split('/').pop()}`,
    variant_id: (variant || product.variants?.[0])?.id.split('/').pop(),
    variant_title: (variant || product.variants?.[0])?.title,
    variant_quantity:
      (variant || product.variants?.[0])?.quantityAvailable || 0 >= 0 ? (variant || product.variants?.[0])?.quantityAvailable : 0,
    product_availability: product.availableForSale,
    variant_availability: (variant || product.variants?.[0])?.availableForSale,
    variant_count: product.variants?.length,
    available_variant_count: availableVariantCount,
    available_variant_ratio: availableVariantCount ? Math.round((availableVariantCount * 100) / (product.variants?.length || 0)) : 0,
    total_stock: product.variants?.reduce((acc, v) => acc + v.quantityAvailable, 0),
  });
};

const AddToCart = (product: Partial<Product>, variant: Variant, quantity: number) => {
  const lastCollection = product.collections?.[product.collections?.length - 1];

  (<any>window).dataLayer.push({
    event: 'ee_addToCart',
    product_name: product.title?.replace(/["']/g, ''),
    product_handle: product.handle,
    product_id: product.id?.split('/').pop(),
    product_price: variant.price?.amount,
    currency: variant.price?.currencyCode,
    product_brand: product.vendor,
    product_type: product.productType,
    category_id: lastCollection?.id.split('/').pop(),
    category_title: lastCollection?.title,
    category_handle: lastCollection?.handle,
    quantity: quantity,
    variant_id: variant.id.split('/').pop(),
    variant_title: variant.title,
    product_sku: variant.sku,
    g_product_id: `shopify_NL_${product.id?.split('/').pop()}_${variant.id.split('/').pop()}`,
  });
};

const RemoveFromCart = (cartLine: Line) => {
  (<any>window).dataLayer.push({
    event: 'ee_removeFromCart',
    product_id: cartLine.merchandise.product.id.split('/').pop(),
    product_name: cartLine.merchandise.product.title?.replace(/["']/g, ''),
    product_handle: cartLine.merchandise.product.handle,
    product_sku: cartLine.merchandise.sku,
    product_type: cartLine.merchandise.product.productType,
    variant_id: cartLine.merchandise.id.split('/').pop(),
    variant_title: cartLine.merchandise.title,
    product_price: cartLine.cost.amountPerQuantity.amount,
    currency: cartLine.cost.amountPerQuantity.currencyCode,
    product_brand: cartLine.merchandise.vendor,
    quantity: cartLine.quantity,
  });
};

const CheckoutStart = (cart: Cart) => {
  const productIds = cart.lines.map(a => a.merchandise.product.id.split('/').pop());
  const productNames = cart.lines.map(a => a.merchandise.product.title?.replace(/["']/g, ''));
  const productHandles = cart.lines.map(a => a.merchandise.product.handle);
  const productTypes = cart.lines.map(a => a.merchandise.product.productType);
  const productSkus = cart.lines.map(a => a.merchandise.sku);
  const productPrices = cart.lines.map(a => a.cost.amountPerQuantity.amount);
  const productQuantities = cart.lines.map(a => a.quantity);
  const productBrands = cart.lines.map(a => a.merchandise.vendor);
  const variantIds = cart.lines.map(a => a.merchandise.id.split('/').pop());
  const variantTitles = cart.lines.map(a => a.merchandise.title);

  (<any>window).dataLayer.push({
    event: 'ee_checkoutStart',
    page_type: 'cart',
    product_id: productIds,
    product_name: productNames,
    product_handle: productHandles,
    product_brand: productBrands,
    product_type: productTypes,
    product_sku: productSkus,
    variant_id: variantIds,
    variant_title: variantTitles,
    product_price: productPrices,
    currency: 'EUR',
    quantity: productQuantities,
    totalValue: cart.cost.totalAmount.amount,
    totalQuantity: cart.lines.reduce((acc, l) => acc + l.quantity, 0),
    g_product_id: cart.lines.map(a => `shopify_NL_${a.merchandise.product.id.split('/').pop()}_${a.merchandise.id.split('/').pop()}`),
  });
};

export const Analyzify = {
  PageViewAsync,
  CollectionPageView,
  ProductPageView,
  AddToCart,
  RemoveFromCart,
  CheckoutStart,
};

async function hashSha256(string: string) {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

async function hashSha1(string: string) {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest('SHA-1', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
  return hashHex;
}
